import { useCallback, useState, useEffect } from 'react';
import { TabPanel, TabPanels, Tabs, Fade, useBreakpointValue } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import { Box } from 'src/components/UI/Box';
import { Flex } from 'src/components/UI/Flex';
import { Image } from 'src/components/UI/Image';

import { PlaceOfServiceStep } from './PlaceOfServiceStep';
import { PreviewStep } from './PreviewStep';
import { ImportDataStep } from './ImportDataStep';
import { OnboardingSuccess } from './OnboardingSuccess';
import { PrescriptionPreview } from './components/PrescriptionPreview';
import {
  type PlaceOfServiceValues,
  type ImportDataValues,
  defaultPlaceOfServiceData,
  defaultImportDataValues,
} from './types';
import { OnboardingService } from 'src/services/onboarding';

import { ReactComponent as Logo } from 'src/assets/images/logo-mevo.svg';
import step1Image from 'src/assets/images/onboarding/step1.png';
import importDataImage from 'src/assets/images/onboarding/import-data.svg';
import backgroundImage from 'src/assets/images/onboarding/background.png';
import mobileAppImage from 'src/assets/images/mobile-app.svg';
import logoTexto from 'src/assets/images/logo-texto.svg';
import signUpMobileBackground from 'src/assets/images/sign-up-mobile-background.png';

import { ImageService } from 'src/services/image';
import { PrescriberService } from 'src/services/prescriber';
import { usePrescriberContext } from 'src/providers/PrescriberProvider';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';
import { waitForAuthorizationHeader } from 'src/api';
import {
  trackOnboardingPlaceOfServiceView,
  trackOnboardingImportDataView,
  trackOnboardingSuccessView,
} from 'src/services/tracking/events/onboarding.events';

export type { PlaceOfServiceValues, ImportDataValues };

export function Onboarding() {
  const navigate = useNavigate();
  const isMobileLayout = useBreakpointValue({ base: true, lg: false }, { ssr: false });
  const [currentStep, setCurrentStep] = useState(0);
  const { prescriberData } = usePrescriberContext();
  const { openToast } = useFeedbackContext();
  const [placeOfServiceData, setPlaceOfServiceData] = useState<PlaceOfServiceValues>(defaultPlaceOfServiceData);
  const [importDataData, setImportDataData] = useState<ImportDataValues>(defaultImportDataValues);
  const [showForm, setShowForm] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    const trackWithAuth = async () => {
      await waitForAuthorizationHeader();

    if (isMobileLayout) {
      if (currentStep === 0) {
        trackOnboardingPlaceOfServiceView();
      } else if (currentStep === 2) {
        trackOnboardingImportDataView();
      } else if (currentStep === 3) {
        trackOnboardingSuccessView();
      }
    } else {
      if (currentStep === 0) {
        trackOnboardingPlaceOfServiceView();
      } else if (currentStep === 1) {
        trackOnboardingImportDataView();
      } else if (currentStep === 2) {
        trackOnboardingSuccessView();
      }
    }
    };

    trackWithAuth();
  }, [currentStep, isMobileLayout]);

  useEffect(() => {
    if (!prescriberData) return;

    const checkOnboardingStatus = async () => {
      try {
        const response = await OnboardingService.listUnfinished();
        const mainOnboarding = response.data.find((item) => item.onboarding === 'MAIN');

        if (!mainOnboarding || mainOnboarding.status === 'FINISHED') {
          navigate('/inicio/receita');
        } else if (mainOnboarding.status === 'NOT_STARTED') {
          sessionStorage.setItem('onboarding-id', mainOnboarding._id);
        }
      } catch (error) {
        console.error('Erro ao verificar status do onboarding:', error);
      }
    };

    checkOnboardingStatus();
  }, [navigate, prescriberData]);

  const handlePlaceOfServiceNext = useCallback((data: PlaceOfServiceValues) => {
    setPlaceOfServiceData(data);
    setCurrentStep(1);
  }, []);

  const handleImportDataNext = useCallback(
    async (data: ImportDataValues) => {
      setImportDataData(data);

      if (!prescriberData || !prescriberData._id) {
        openToast('Erro: dados do prescritor não encontrados.', { type: 'error' });
        return;
      }

      try {
        const payload: any = {
          ...prescriberData,
          askedForMigrationAssist: data.needsHelp,
          attendanceAddress: {
            ...prescriberData.attendanceAddress,
            placeName: placeOfServiceData.placeName,
            postalCode: placeOfServiceData.postalCode,
            address: placeOfServiceData.address,
            houseNumber: placeOfServiceData.houseNumber,
            district: placeOfServiceData.district,
            complement: placeOfServiceData.complement,
            city: placeOfServiceData.city,
            state: placeOfServiceData.state,
            phoneNumber: placeOfServiceData.phoneNumber,
          },
        };

        await PrescriberService.updatePrescriber(prescriberData._id, payload);

        if (logoPreview) {
          try {
            const base64Response = await fetch(logoPreview);
            const blob = await base64Response.blob();
            const file = new File([blob], 'logo.png', { type: 'image/png' });

            const formData = new FormData();
            formData.append('file', file);
            formData.append('height', '80');

            await ImageService.uploadImage(formData, prescriberData._id);
          } catch (uploadError) {
            console.error('Erro ao fazer upload da imagem:', uploadError);
          }
        }

        const onboardingId = sessionStorage.getItem('onboarding-id');
        if (onboardingId) {
          try {
            await OnboardingService.finish(onboardingId);
            sessionStorage.removeItem('onboarding-id');
          } catch (finishError) {
            console.error('Erro ao finalizar onboarding:', finishError);
          }
        }

        localStorage.setItem('onboarding-completed', 'true');

        setCurrentStep(isMobileLayout ? 3 : 2);
      } catch (error) {
        console.error('Erro ao salvar dados do onboarding:', error);
        openToast('Erro ao salvar seus dados. Por favor, tente novamente.', { type: 'error' });
      }
    },
    [placeOfServiceData, logoPreview, prescriberData, openToast, isMobileLayout]
  );

  const handlePreviewNext = useCallback(() => {
    setCurrentStep(2);
  }, []);

  const handlePreviewBack = useCallback(() => {
    setCurrentStep(0);
  }, []);

  const handleImportDataBack = useCallback(() => {
    setCurrentStep(isMobileLayout ? 1 : 0);
  }, [isMobileLayout]);

  const handleSuccessBack = useCallback(() => {
    setCurrentStep(isMobileLayout ? 2 : 1);
  }, [isMobileLayout]);

  const handleFinish = useCallback(() => {
    navigate('/inicio/receita');
  }, [navigate]);

  const handlePlaceOfServiceDataChange = useCallback(
    (data: PlaceOfServiceValues, formVisible: boolean, logo: string | null) => {
      setPlaceOfServiceData(data);
      setShowForm(formVisible);
      setLogoPreview(logo);
    },
    []
  );

  return (
    <Box w="100%" minH="100vh" bg="white" position="relative" overflow="hidden">
      <Box
        display={{ base: 'none', lg: 'flex', xl: 'flex' }}
        justifyContent={'center'}
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex="999"
        bg="white"
        p="40px 0 20px 0"
      >
        <Box
          ml={{
            base: '20px',
            lg: '20px',
          }}
          maxW={'1250px'}
          width={'100%'}
        >
          <Logo height={'22px'} fill={'#3D0E6B'} />
        </Box>
      </Box>

      <Flex display={{ base: 'none', lg: 'flex' }} w="100%" h="100vh" justifyContent="center">
        <Flex w="100%" maxW="1440px" h="100%" gap="40px">
          <Flex w="50%" h="100%" flexDir="column" p="102px 0 40px 40px" overflow="hidden">
            <Box w="100%" maxW={'520px'} mx="auto" flex="1" display="flex" flexDir="column" overflow="hidden">
              <Tabs index={currentStep} variant="unstyled" isLazy={false} h="100%" display="flex" flexDir="column">
                <TabPanels flex="1" overflow="hidden">
                  <TabPanel p={0} m={0} h="100%">
                    <PlaceOfServiceStep
                      initialData={placeOfServiceData}
                      onNext={handlePlaceOfServiceNext}
                      onDataChange={handlePlaceOfServiceDataChange}
                    />
                  </TabPanel>

                  <TabPanel p={0} m={0} h="100%">
                    <ImportDataStep
                      initialData={importDataData}
                      onNext={handleImportDataNext}
                      onBack={handleImportDataBack}
                    />
                  </TabPanel>

                  <TabPanel p={0} m={0} h="100%">
                    <OnboardingSuccess onFinish={handleFinish} onBack={handleSuccessBack} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Flex>

          <Flex w="50%" h="100%" justifyContent="center" alignItems="center" p="40px 40px 40px 0" position="relative">
            {currentStep === 0 && (
              <>
                <Fade in={!showForm} unmountOnExit style={{ position: 'absolute', width: '100%', height: '100%' }}>
                  <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
                    <Image
                      draggable={false}
                      src={step1Image}
                      alt="Onboarding Step 1"
                      maxW="90%"
                      maxH="90%"
                      objectFit="contain"
                      borderRadius="16px"
                    />
                  </Flex>
                </Fade>

                <Fade in={showForm} unmountOnExit style={{ position: 'absolute', width: '100%', height: '100%' }}>
                  <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
                    <Box
                      w="600px"
                      h="100%"
                      maxH="650px"
                      backgroundImage={`url(${backgroundImage})`}
                      backgroundSize="cover"
                      backgroundPosition="center"
                      backgroundRepeat="no-repeat"
                      display="flex"
                      justifyContent="center"
                      alignItems="flex-end"
                      borderRadius="20px"
                    >
                      <PrescriptionPreview data={placeOfServiceData} logoPreview={logoPreview} />
                    </Box>
                  </Flex>
                </Fade>
              </>
            )}

            {currentStep === 1 && (
              <Fade in={true} style={{ width: '100%', height: '100%' }}>
                <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
                  <Image
                    draggable={false}
                    src={importDataImage}
                    alt="Import Data"
                    maxW="90%"
                    maxH="90%"
                    objectFit="contain"
                    borderRadius="16px"
                  />
                </Flex>
              </Fade>
            )}

            {currentStep === 2 && (
              <Fade in={true} style={{ width: '100%', height: '100%' }}>
                <Flex
                  mt={'80px'}
                  h="80%"
                  w="80%"
                  justifyContent="center"
                  alignItems="center"
                  backgroundImage={`url(${signUpMobileBackground})`}
                  backgroundSize="cover"
                  backgroundPosition="center"
                  backgroundRepeat="no-repeat"
                  borderRadius="30px"
                  flexDirection="column"
                >
                  <Image
                    mt={'20px'}
                    src={logoTexto}
                    draggable="false"
                    alt="Mevo Receita Digital"
                    h="10%"
                    maxW="100%"
                    objectFit="contain"
                  />
                  <Image
                    src={mobileAppImage}
                    draggable="false"
                    alt="Mevo App"
                    h="100%"
                    maxW="100%"
                    objectFit="contain"
                  />
                </Flex>
              </Fade>
            )}
          </Flex>
        </Flex>
      </Flex>

      <Box
        display={{ base: 'block', lg: 'none' }}
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex="999"
        bg="white"
        p="20px"
        boxShadow="sm"
      >
        <Logo height={'28px'} fill={'#3D0E6B'} />
      </Box>

      <Flex display={{ base: 'flex', lg: 'none' }} w="100%" h="100vh" flexDir="column" pt="68px">
        <Box
          w="100%"
          h={{ base: '0px', md: '200px' }}
          bg="linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)"
          display={isMobileLayout ? 'none' : 'flex'}
          justifyContent="center"
          alignItems="center"
          p="20px"
        >
          <Image
            draggable={false}
            src={currentStep === 0 ? step1Image : importDataImage}
            alt={currentStep === 0 ? 'Onboarding Step 1' : 'Import Data'}
            maxW="80%"
            maxH="160px"
            objectFit="contain"
            borderRadius="12px"
          />
        </Box>

        <Flex flex="1" w="100%" flexDir="column" p="24px" pb="40px" overflowY="auto">
          <Tabs index={currentStep} variant="unstyled" isLazy={false}>
            <TabPanels>
              <TabPanel p={0} m={0}>
                <PlaceOfServiceStep
                  initialData={placeOfServiceData}
                  onNext={handlePlaceOfServiceNext}
                  onDataChange={handlePlaceOfServiceDataChange}
                />
              </TabPanel>

              <TabPanel p={0} m={0}>
                <PreviewStep
                  data={placeOfServiceData}
                  logoPreview={logoPreview}
                  onNext={handlePreviewNext}
                  onBack={handlePreviewBack}
                />
              </TabPanel>

              <TabPanel p={0} m={0}>
                <ImportDataStep
                  initialData={importDataData}
                  onNext={handleImportDataNext}
                  onBack={handleImportDataBack}
                />
              </TabPanel>

              <TabPanel p={0} m={0}>
                <OnboardingSuccess onFinish={handleFinish} onBack={handleSuccessBack} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Flex>
      </Flex>
    </Box>
  );
}
